import { ArrowUpRight, Ellipsis, Layers, Lock, Rocket, Settings2, Snowflake, Undo2, X } from '@orgus/ui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { DEPLOYMENT_STATUS_LABEL, describeFreeze, freezeActive } from '@orgus/protocol';
import type { Deployment, EnvironmentView, Id, LandingProjectConfig, LandingState, Project } from '@orgus/protocol';
import { Badge, Button, ConfirmDialog, DataTable, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, EmptyState, ErrorState, IconButton, Pressable, ProjectMark, RelativeTime, RowStatus, Skeleton, Tooltip, cn, formatInt, toast, useNow } from '@orgus/ui';
import type { Column } from '@orgus/ui';
import type { ReactNode } from 'react';
import { useRegisterCommands } from '../../lib/commands.ts';
import type { Command } from '../../lib/commands.ts';
import { useOrgus } from '../../lib/store.ts';
import { projectConfig, removeFreeze, useLanding, useMockLandingTicker } from './api.ts';
import { DeployDialog } from './DeployDialog.tsx';
import type { DeployIntent } from './DeployDialog.tsx';
import { FreezeDialog } from './FreezeDialog.tsx';
import { deploymentRowStatus, envKindLabel, environmentProjects, projectEnvironments, promoteSource } from './model.ts';
import { Notice } from './Notice.tsx';
import { ReportedEnvironments } from './ReportedEnvironments.tsx';
import type { ReportedTarget } from './ReportedEnvironments.tsx';
import { PipelinesTabs, TabMenu } from './PipelinesTabs.tsx';
import { useProjectProtocol } from '../project-health/api.ts';
import { reportsForProject } from '../project-health/model.ts';

const KIND_LABEL: Record<Deployment['kind'], string> = { deploy: 'Deploy', promote: 'Promote', rollback: 'Rollback' };
/** What happened, for a card's line: "Deployed by landing-queue", "Promoted from staging by owner". */
const KIND_DONE: Record<Deployment['kind'], string> = { deploy: 'Deployed', promote: 'Promoted', rollback: 'Rolled back' };

/** An environment's name in sentence case, the one casing on cards, the table and reported rows ("production" reads "Production"). */
export function envName(name: string): string {
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : name;
}

/** A heading at the level its place needs: grouped by project, every card sits one level deeper. */
function Heading({ level, className, children }: { level: 2 | 3 | 4; className?: string; children: ReactNode }) {
  const Tag = `h${level}` as 'h2';
  return <Tag className={className}>{children}</Tag>;
}

function EnvSkeleton() {
  return (
    <div className="flex flex-col gap-4" role="status" aria-label="Loading environments">
      <Skeleton className="h-12 w-96" />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-56 rounded-card" />
        ))}
      </div>
      <Skeleton className="h-64 rounded-card" />
    </div>
  );
}

/** One environment: what is live, where it came from, and the actions that change it. */
function EnvCard({
  level,
  view,
  source,
  landingTitle,
  onIntent,
  onFreeze,
  onUnfreeze,
  onOpenLanding,
}: {
  level: 2 | 3;
  view: EnvironmentView;
  source: EnvironmentView | null;
  landingTitle: (id: Id | null) => string | null;
  onIntent: (i: DeployIntent) => void;
  onFreeze: () => void;
  onUnfreeze: (id: Id, reason: string) => void;
  onOpenLanding: (id: Id) => void;
}) {
  const now = useNow(30_000);
  const c = view.config;
  const cur = view.current;
  const busy = view.deploying;
  const canPromote = !!source?.current && source.current.version !== cur?.version;
  const title = cur ? landingTitle(cur.landingId) : null;
  const kind = envKindLabel(c);
  return (
    <section aria-label={`${c.name} environment`} className="flex min-w-0 flex-col rounded-card border border-border bg-surface">
      <header className="flex h-row items-center gap-2 border-b border-border-subtle px-4">
        <Heading level={level} className="truncate text-ui font-medium text-fg">
          {envName(c.name)}
        </Heading>
        {kind.toLowerCase() !== c.name.toLowerCase() && <span className="truncate text-caption text-fg-subtle">{kind}</span>}
        {c.protected && (
          <Tooltip content="Protected: an admin deploys, typing the exact version">
            <span tabIndex={0} role="img" className="flex items-center text-fg-subtle" aria-label="Protected">
              <Lock className="size-4" aria-hidden />
            </span>
          </Tooltip>
        )}
        {view.frozen && (
          <Tooltip content={`${view.frozen.reason}. ${describeFreeze(view.frozen)}`}>
            <span tabIndex={0}>
              <Badge tone="waiting" icon={<Snowflake />}>
                Frozen
              </Badge>
            </span>
          </Tooltip>
        )}
        {/* How it gets new versions, labelled: the tooltip says what the word means. */}
        <Tooltip content={c.autoDeployFrom ? `Promotion: every landing on ${c.autoDeployFrom} deploys here` : 'Promotion: manual, a person deploys or promotes'}>
          <span tabIndex={0} className="ml-auto shrink-0 rounded-control text-caption text-fg-subtle">
            Promotion <span className="text-fg-muted">{c.autoDeployFrom ? `follows ${c.autoDeployFrom}` : 'manual'}</span>
          </span>
        </Tooltip>
      </header>

      <div className="flex flex-1 flex-col gap-3 p-4">
        {busy && (
          <p className="flex items-center gap-2 text-ui">
            <RowStatus tone="working" icon="working" label={busy.kind === 'rollback' ? 'Rolling back to' : 'Deploying'} />
            <span className="font-mono text-fg">{busy.version}</span>
            <RelativeTime ts={busy.startedAt} className="ml-auto text-caption text-fg-subtle num" />
          </p>
        )}
        {cur ? (
          <div className="flex flex-col gap-1">
            <p className="flex items-center gap-3">
              <span className="font-mono text-title font-semibold text-fg">{cur.version}</span>
              <RowStatus {...deploymentRowStatus(cur)} />
            </p>
            {title && cur.landingId ? (
              <Pressable onClick={() => onOpenLanding(cur.landingId!)} className="flex max-w-full items-center gap-1 rounded-control text-left text-ui text-fg-muted hover:text-fg">
                <span className="truncate">{title}</span>
                <ArrowUpRight className="size-4 shrink-0" aria-hidden />
              </Pressable>
            ) : (
              <p className="text-ui text-fg-subtle">No landing linked</p>
            )}
            <p className="text-caption text-fg-subtle">
              {KIND_DONE[cur.kind]}
              {cur.fromEnv ? ` from ${cur.fromEnv}` : ''} by {cur.by}, <RelativeTime ts={cur.endedAt ?? cur.startedAt} />
            </p>
          </div>
        ) : (
          <p className="text-ui text-fg-muted">Nothing live yet</p>
        )}

        {/* A freeze shows only while one is set: one quiet line each, removable. */}
        {c.freezes.length > 0 && (
          <ul className="flex flex-col gap-1" aria-label="Freeze windows">
            {c.freezes.map((w) => (
              <li key={w.id} className="flex items-start gap-2 text-caption">
                <span className="flex h-4 shrink-0 items-center">
                  <Snowflake className={cn('size-4', freezeActive(w, now) ? 'text-status-attention' : 'text-fg-subtle')} aria-hidden />
                </span>
                {/* The reason on one line, when it holds on the next: never cut, it is the rule a deploy waits for. */}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="break-words text-fg-muted">{w.reason}</span>
                  <span className="text-fg-subtle">
                    {describeFreeze(w)}
                    {freezeActive(w, now) ? ', in force now' : ''}
                  </span>
                </span>
                <IconButton size="xs" label={`Remove the freeze "${w.reason}"`} icon={<X />} onClick={() => onUnfreeze(w.id, w.reason)} />
              </li>
            ))}
          </ul>
        )}
        {view.frozen && <p className="text-caption text-status-attention">Deploys and promotes wait for the freeze; a rollback still runs.</p>}
      </div>

      {/* One footer row in every card, so the buttons line up across the row and never wrap: the main action, then roll back, the rest in the menu. */}
      <footer className="flex min-w-0 items-center gap-2 border-t border-border-subtle px-4 py-3">
        {source ? (
          <Button size="sm" variant={c.kind === 'production' ? 'primary' : 'secondary'} icon={<Rocket />} className="min-w-0" disabled={!canPromote || !!busy || !!view.frozen} onClick={() => onIntent({ kind: 'promote', env: view, from: source })}>
            {canPromote ? `Promote ${source.current!.version}` : `Promote from ${source.config.name}`}
          </Button>
        ) : (
          <Button size="sm" variant="secondary" icon={<Rocket />} className="min-w-0" disabled={!!busy || !!view.frozen} onClick={() => onIntent({ kind: 'deploy', env: view })}>
            Deploy version
          </Button>
        )}
        {view.previous && (
          <Button size="sm" variant="ghost" icon={<Undo2 />} className="min-w-0 max-sm:hidden" disabled={!!busy} onClick={() => onIntent({ kind: 'rollback', env: view, to: view.previous!.version })}>
            Roll back to {view.previous.version}
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton size="sm" className="ml-auto" label={`More ${c.name} actions`} icon={<Ellipsis />} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {source && (
              <DropdownMenuItem icon={<Rocket />} disabled={!!busy || !!view.frozen} onSelect={() => onIntent({ kind: 'deploy', env: view })}>
                Deploy a version{'…'}
              </DropdownMenuItem>
            )}
            {view.previous && (
              // On a phone the footer keeps one button; rolling back moves here.
              <DropdownMenuItem icon={<Undo2 />} className="sm:hidden" disabled={!!busy} onSelect={() => onIntent({ kind: 'rollback', env: view, to: view.previous!.version })}>
                Roll back to {view.previous.version}{'…'}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem icon={<Snowflake />} onSelect={onFreeze}>
              Add a freeze window{'…'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </footer>
    </section>
  );
}

/** One project's environments: the ones it reports and, when it uses the landing queue, the queue's. */
interface ProjectGroup {
  project: Project;
  reported: ReportedTarget[];
  /** The queue's environments show for a project that uses the queue, or that reports none of its own. */
  queue: boolean;
  config: LandingProjectConfig;
  views: EnvironmentView[];
}

const ALL = 'all';

/**
 * The Environments tab: what is live where, promote and rollback, freezes and the deployment
 * history. By default every project's environments, grouped by project: the same set the
 * Overview's flow shows. The picker narrows it to one project (`?project=`).
 */
export function EnvironmentsView() {
  const load = useLanding();
  useMockLandingTicker();
  const projects = useOrgus((st) => st.projects);
  const machines = useOrgus((st) => st.machines);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [intent, setIntent] = useState<{ projectId: Id; intent: DeployIntent } | null>(null);
  const [freezeEnv, setFreezeEnv] = useState<{ projectId: Id; env: string } | null>(null);
  const [unfreeze, setUnfreeze] = useState<{ projectId: Id; env: string; id: Id; reason: string } | null>(null);
  const now = useNow(10_000);
  const s: LandingState | undefined = load.state;
  const pp = useProjectProtocol().state;

  // Projects that use the queue (saved settings or a queue), and projects that report their own
  // environments (Project Protocol deploys.json), such as Local main.
  const using = useMemo(() => (s ? [...new Set([...s.configs.map((c) => c.projectId), ...s.queues.map((q) => q.projectId)])].filter((id) => projects[id]) : []), [s, projects]);
  const reporting = useMemo(() => [...new Set((pp?.projects ?? []).filter((r) => r.deploys.length).map((r) => r.projectId))].filter((id) => projects[id]), [pp, projects]);
  const listed = useCallback((id: Id) => using.includes(id) || reporting.includes(id), [using, reporting]);
  const picked = params.get('project');
  const scope: Id | null = picked && projects[picked] ? picked : null;

  const groups = useMemo<ProjectGroup[]>(() => {
    if (!s) return [];
    return environmentProjects(projects, listed, scope).map((project) => {
      const reported = reportsForProject(pp, project.id).flatMap((r) =>
        r.deploys.map((t) => ({ t, machine: machines[r.machineId]?.name ?? 'Unknown machine', updatedAt: r.files.find((f) => f.kind === 'deploys')?.updatedAt ?? null })),
      );
      const config = projectConfig(s, project.id, project.defaultBranch);
      return { project, reported, queue: using.includes(project.id) || !reported.length, config, views: projectEnvironments(s, config, now) };
    });
  }, [s, scope, projects, listed, pp, machines, using, now]);
  const groupOf = (id: Id | undefined) => groups.find((g) => g.project.id === id);
  const queueIds = useMemo(() => new Set(groups.filter((g) => g.queue).map((g) => g.project.id)), [groups]);
  const history = useMemo(() => (s ? s.deployments.filter((d) => queueIds.has(d.projectId)) : []), [s, queueIds]);
  const landingTitle = useCallback((id: Id | null) => (id ? (s?.landings.find((l) => l.id === id)?.title ?? null) : null), [s]);
  const setProject = (id: string) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id === ALL) n.delete('project');
        else n.set('project', id);
        return n;
      },
      { replace: true },
    );
  const openLanding = (id: Id) => {
    const l = s?.landings.find((x) => x.id === id);
    navigate(`/pipelines/landing?${new URLSearchParams({ ...(l ? { queue: l.queueId } : {}), landing: id }).toString()}`);
  };
  const landingSettings = (id?: Id) => navigate(id ? `/settings/landing-queue?project=${encodeURIComponent(id)}` : '/settings/landing-queue');

  const cmds = useMemo<Command[]>(() => {
    const out: Command[] = [];
    for (const g of groups) {
      if (!g.queue) continue;
      const pid = g.project.id;
      for (const v of g.views) {
        const src = promoteSource(g.views, v.config.name);
        if (src?.current && src.current.version !== v.current?.version) {
          out.push({ id: `env.promote:${pid}:${v.config.name}`, title: `Promote ${src.current.version} to ${v.config.name}`, subtitle: g.project.name, group: 'Environments', icon: Rocket, perform: () => setIntent({ projectId: pid, intent: { kind: 'promote', env: v, from: src } }) });
        }
        if (v.previous) out.push({ id: `env.rollback:${pid}:${v.config.name}`, title: `Roll ${v.config.name} back to ${v.previous.version}`, subtitle: g.project.name, group: 'Environments', icon: Undo2, perform: () => setIntent({ projectId: pid, intent: { kind: 'rollback', env: v, to: v.previous!.version } }) });
      }
    }
    return out;
  }, [groups]);
  useRegisterCommands(cmds, [cmds]);

  const projectName = (d: Deployment) => projects[d.projectId]?.name ?? 'Unknown project';
  const columns: Column<Deployment>[] = [
    { key: 'when', header: 'When', type: 'date', cell: (d) => <RelativeTime ts={d.startedAt} />, sortValue: (d) => d.startedAt },
    ...(scope ? [] : [{ key: 'project', header: 'Project', cell: projectName, text: projectName, hideBelow: 'md' as const }]),
    { key: 'env', header: 'Environment', cell: (d) => envName(d.env), text: (d) => d.env, hideBelow: 'sm' },
    { key: 'version', header: 'Version', cell: (d) => <span className="font-mono">{d.version}</span>, text: (d) => d.version },
    {
      key: 'landing',
      header: 'From landing',
      grow: true,
      // Below 768 the table keeps When, Environment, Version and Status; below 640, When, Version and Status, which fit a phone.
      hideBelow: 'md',
      cell: (d) => <span className="truncate text-fg-muted">{landingTitle(d.landingId) ?? 'Not linked'}</span>,
      text: (d) => landingTitle(d.landingId) ?? '',
    },
    { key: 'kind', header: 'Kind', cell: (d) => `${KIND_LABEL[d.kind]}${d.fromEnv ? ` from ${d.fromEnv}` : ''}`, hideBelow: 'lg' },
    {
      key: 'status',
      header: 'Status',
      cell: (d) => (
        <Tooltip content={d.problem ? `${d.problem.what} ${d.problem.why}` : ''} enabled={!!d.problem}>
          <span tabIndex={d.problem ? 0 : -1}>
            <RowStatus {...deploymentRowStatus(d)} />
          </span>
        </Tooltip>
      ),
      text: (d) => DEPLOYMENT_STATUS_LABEL[d.status],
    },
    { key: 'by', header: 'By', cell: (d) => d.by, text: (d) => d.by, hideBelow: 'lg' },
  ];

  /** A project's name with its mark, as a heading: the reported panel's header in the all-projects view. */
  const projectTitle = (p: Project) => (
    <span className="flex items-center gap-2">
      <ProjectMark projectId={p.id} name={p.name} size="xs" />
      {p.name}
    </span>
  );

  /** One project: what it reports as a list, then the queue's environments with their actions. */
  const renderGroup = (g: ProjectGroup) => {
    const { project, config, views } = g;
    const noDeploy = !config.deployCommand && !config.environments.some((e) => e.deployCommand);
    return (
      <>
        <ReportedEnvironments title={scope ? `Reported by ${project.name}` : 'Reported environments'} label={`Environments ${project.name} reports`} items={g.reported} />
        {g.queue && noDeploy && (
          <Notice
            title={`No deploy command is set for ${project.name}.`}
            action={
              <Button size="sm" variant="secondary" onClick={() => landingSettings(project.id)}>
                Set it
              </Button>
            }
          >
            Landings still land; nothing deploys until one is set.
          </Notice>
        )}
        {g.queue && (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {views.map((v) => (
              <EnvCard
                key={v.config.name}
                level={scope ? 2 : 3}
                view={v}
                source={promoteSource(views, v.config.name)}
                landingTitle={landingTitle}
                onIntent={(i) => setIntent({ projectId: project.id, intent: i })}
                onFreeze={() => setFreezeEnv({ projectId: project.id, env: v.config.name })}
                onUnfreeze={(id, reason) => setUnfreeze({ projectId: project.id, env: v.config.name, id, reason })}
                onOpenLanding={openLanding}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  let body;
  // The project picker, in the page's one header row beside the tabs.
  let header: ReactNode = null;
  if (load.phase === 'loading') body = <EnvSkeleton />;
  else if (load.phase === 'error' || !s) {
    body = (
      <div className="rounded-card border border-border bg-surface">
        <ErrorState error={load.error ?? { what: 'Environments did not load.', why: 'The hub did not send them.', next: 'Try again.' }} onRetry={load.retry} />
      </div>
    );
  } else if (!groups.length) {
    body = (
      <div className="rounded-card border border-border bg-surface">
        <EmptyState
          size="page"
          icon={<Layers />}
          title="No environments yet"
          description="Environments appear once a project uses the landing queue or reports its own."
          action={
            <Button variant="primary" icon={<Settings2 />} onClick={() => landingSettings()}>
              Landing settings
            </Button>
          }
        />
      </div>
    );
  } else {
    // The breadcrumb's workspace scopes the page; a project heading narrows it to that project
    // (`?project=`), shown here as one chip that clears back to every project. Settings sit in the
    // tab menu, the last control on every Pipelines tab.
    header = (
      <>
        {scope && projects[scope] && (
          <span className="flex min-w-0 items-center gap-2 rounded-control text-ui">
            <ProjectMark projectId={scope} name={projects[scope]!.name} size="xs" />
            <span className="min-w-0 truncate font-medium text-fg">{projects[scope]!.name}</span>
            <IconButton size="xs" variant="ghost" label="Show every project" icon={<X />} onClick={() => setProject(ALL)} />
          </span>
        )}
        <TabMenu label="More environment controls" items={[{ label: 'Landing settings', icon: <Settings2 />, onSelect: () => landingSettings(scope ?? undefined) }]} />
      </>
    );
    body = (
      <div className="flex flex-col gap-8">
        {scope ? (
          <div className="flex flex-col gap-4">{renderGroup(groups[0]!)}</div>
        ) : (
          groups.map((g) => (
            <section key={g.project.id} aria-label={g.project.name} className="flex flex-col gap-3">
              {/* One header for every project; what it reports is a section inside it, beside the queue's environments. */}
              <h2 className="text-ui font-medium text-fg">
                {groups.length > 1 ? (
                  <Tooltip content={`Show only ${g.project.name}`}>
                    <Pressable onClick={() => setProject(g.project.id)} className="w-auto rounded-control hover:text-accent-text">
                      {projectTitle(g.project)}
                    </Pressable>
                  </Tooltip>
                ) : (
                  projectTitle(g.project)
                )}
              </h2>
              {renderGroup(g)}
            </section>
          ))
        )}
        {queueIds.size > 0 && (
          <section aria-labelledby="deploy-history">
            <h2 id="deploy-history" className="mb-2 text-ui font-medium text-fg">
              Deployments
            </h2>
            <DataTable
              label={scope ? `Deployments of ${projects[scope]!.name}` : 'Deployments'}
              rows={history}
              columns={columns}
              rowKey={(d) => d.id}
              searchable={{ placeholder: 'Search deployments' }}
              initialSort={{ key: 'when', dir: 'desc' }}
              pageSize={15}
              onRowClick={(d) => d.landingId && openLanding(d.landingId)}
              rowLabel={(d) => `Open the landing behind ${d.version}`}
              empty={<EmptyState icon={<Rocket />} title="No deployments yet" description="Deploys, promotes and rollbacks show here." />}
            />
          </section>
        )}
      </div>
    );
  }

  const intentGroup = groupOf(intent?.projectId);
  const freezeGroup = groupOf(freezeEnv?.projectId);
  return (
    <>
      <PipelinesTabs fit={!scope}>{header}</PipelinesTabs>
      {body}
      {s && intentGroup && <DeployDialog intent={intent?.intent ?? null} onClose={() => setIntent(null)} state={s} config={intentGroup.config} projectName={intentGroup.project.name} />}
      {freezeGroup && <FreezeDialog env={freezeEnv?.env ?? null} config={freezeGroup.config} onClose={() => setFreezeEnv(null)} />}
      <ConfirmDialog
        open={!!unfreeze}
        onOpenChange={(v) => !v && setUnfreeze(null)}
        tone="primary"
        title={`Remove the ${unfreeze?.env ?? ''} freeze "${unfreeze?.reason ?? ''}"?`}
        description="Deploys and promotes to this environment are allowed again unless another window is in force."
        confirmLabel="Remove freeze"
        onConfirm={async () => {
          const g = groupOf(unfreeze?.projectId);
          if (!unfreeze || !g) return;
          await removeFreeze(g.project.id, unfreeze.env, unfreeze.id, g.config);
          toast.success('Freeze removed', `${unfreeze.env}: ${unfreeze.reason}`);
        }}
      />
    </>
  );
}
